import {
    Box,
    HStack,
    VStack,
    Flex,
    Alert,
    AlertIcon,
    Text,
    Button,
    Spinner,
    Icon,
} from '@chakra-ui/react';
import { useState, useCallback, useMemo, useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetOrdersFeedPaginated, Phase, AllOrdersEntry, NewOrderEntry, QuotingEntry, ReadyToOrderEntry, OrderedEntry, CompletedEntry, OrderFeedEntry, ReKeyingEntry } from '../../hooks/useGetOrdersFeed';

import { AdditionalFilters } from '../../hooks/useHomeFeed';
import { AllOrdersTable, ArchivedOrdersTable, NewOrderTable, QuotingTable, SelectionTable, OrderedTable, CompletedTable, RekeyingTable } from '../../components/orders';
import RefreshButton from '../../components/RefreshButton';
import LocationSelector from '../../components/LocationSelector';
import TableSettingsButton from '../../components/TableSettingsButton';
import CollapsibleSearch, { LIST_PAGE_SEARCH_WIDTH } from '../../components/CollapsibleSearch';
import { PendingCountBadge } from '../../components/PendingCountBadge';
import ListTabRow from '../../components/ListTabRow';
import { TbArchive, TbBuilding } from 'react-icons/tb';
import { FiFileText, FiDollarSign, FiShoppingCart, FiPackage, FiAlertTriangle, FiRefreshCw, FiCheckCircle, FiList } from 'react-icons/fi';
import { useResponsiveToolbar } from '../../hooks/useResponsiveToolbar';
import usePageTitle from '../../hooks/usePageTitle';
import Pagination from '../../components/Pagination';
import { SortState, DashboardSortField, toggleSort } from '../../components/orders/components/SortableTableHeader';
import { useRecentActivity } from '../../hooks/useRecentActivity';
import AdvancedFilters from '../../components/AdvancedFilters';
import SessionContext from '../../contexts/SessionContext';
import { useTableSettings } from '../../hooks/useTableSettings';
import { useScrollShadow } from '../../hooks/useScrollShadow';
import { useColumnWidths } from '../../hooks/useColumnWidths';
import { areAdditionalFiltersEqual, areLocationSelectionsEqual, parseCachedAdditionalFilters } from '../../utils/viewStateUtils';
import { recallListSearch } from '../../shared/listSearchMemory';
import { useRememberListSearch } from '../../hooks/useRememberListSearch';
import { useStickyTableCopyFront } from '../../hooks/useStickyTableCopyFront';
import { stickyTableScrollCopySx } from '../../utils/tableCopySx';
import { useOrderBulkSelection } from '../../hooks/useOrderBulkSelection';
import { useOrderBulkActions } from '../../hooks/useOrderBulkActions';
import OrderBulkActionBar from '../../components/orders/OrderBulkActionBar';
import {
    OrderBulkListContext,
    OrderBulkPermissions,
    getOrderBulkPermissions,
} from '../../utils/orderBulkActions';
import type { DashboardBulkSelection } from '../../components/orders/dashboardBulkSelection';

const TAB_TO_COLUMN_KEY: Record<string, string> = {
    'all': 'dashboard_all',
    'archived': 'dashboard_archived',
    'new-order': 'dashboard_new-order',
    'in-quoting': 'dashboard_in-quoting',
    'ready-to-order': 'dashboard_ready-to-order',
    'rejected': 'dashboard_ready-to-order',
    'ordered': 'dashboard_ordered',
    'completed': 'dashboard_completed',
    're-keying': 'dashboard_re-keying',
};

const defaultSortState: SortState = { field: null, direction: 'asc' };
type DashboardActiveTab = Phase | 'all' | 'archived' | 'completed' | 're-keying';
const DASHBOARD_VIEW_STATE_CACHE_KEY = 'dashboard_view_state_cache';

interface DashboardViewStateCache {
    locationIds: Array<string> | 'all';
    activeTab: DashboardActiveTab;
    currentPage: number;
    searchQuery: string;
    debouncedSearch: string;
    additionalFilters: AdditionalFilters;
    sortState: SortState;
}

const loadDashboardViewStateCache = (): DashboardViewStateCache | null => {
    if (typeof window === 'undefined') return null;

    try {
        const saved = sessionStorage.getItem(DASHBOARD_VIEW_STATE_CACHE_KEY);
        if (!saved) return null;

        const parsed = JSON.parse(saved) as DashboardViewStateCache;
        return {
            ...parsed,
            additionalFilters: parseCachedAdditionalFilters(parsed.additionalFilters ?? {}),
        };
    } catch {
        return null;
    }
};

// Helper to load sort state from localStorage for a specific tab
const loadSortState = (tab: string): SortState => {
    try {
        const saved = localStorage.getItem(`dashboard_sort_${tab}`);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed && typeof parsed.field !== 'undefined' && parsed.direction) {
                return parsed as SortState;
            }
        }
    } catch {
        // Ignore parsing errors
    }
    return defaultSortState;
};

// Helper to save sort state to localStorage for a specific tab
const saveSortState = (tab: string, state: SortState) => {
    try {
        localStorage.setItem(`dashboard_sort_${tab}`, JSON.stringify(state));
    } catch {
        // Ignore storage errors
    }
};

// Helper to parse days string like "< 1 day", "1 days", etc.
const parseDays = (daysStr: string | undefined): number => {
    if (!daysStr) return 0;
    if (daysStr.includes('<')) return 0;
    const match = daysStr.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 0;
};

// Helper to parse delivery time strings like "1-2 days", "overnight", etc.
const parseDeliveryTime = (deliveryStr: string | undefined): number => {
    if (!deliveryStr) return Infinity; // Put empty at the end
    const lowerStr = deliveryStr.toLowerCase();
    // Handle special cases first
    if (lowerStr.includes('same day')) return 0;
    if (lowerStr.includes('overnight')) return 1; // Overnight is next day, so 1 day
    // Try to extract numbers from strings like "1-2 days", "2 days", etc.
    const match = deliveryStr.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : Infinity;
};

// Helper to parse ETA date strings like "Mon, 12/15", "Tue, 12/16"
// Used for the "Ordered" tab where ETA is a specific date
const parseEtaDate = (etaStr: string | undefined): number => {
    if (!etaStr) return Infinity; // Put empty at the end

    // Parse date format like "Mon, 12/15" or just "12/15"
    const dateMatch = etaStr.match(/(\d{1,2})\/(\d{1,2})/);
    if (dateMatch) {
        const month = parseInt(dateMatch[1], 10);
        const day = parseInt(dateMatch[2], 10);
        const now = new Date();
        const currentYear = now.getFullYear();

        // Create date for this year
        let etaDate = new Date(currentYear, month - 1, day);

        // If the date is more than 30 days in the past, assume it's next year
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        if (etaDate < thirtyDaysAgo) {
            etaDate = new Date(currentYear + 1, month - 1, day);
        }

        // Return days from today (can be negative for past dates)
        const diffTime = etaDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    }

    // If no date pattern found, put at end
    return Infinity;
};

const Orders = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const session = useContext(SessionContext);
    const { trackView } = useRecentActivity();
    const { settings: tableSettings, updateSettings: updateTableSettings, tableSettingsSx } = useTableSettings('dashboard');
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { showRightShadow } = useScrollShadow(scrollContainerRef);
    const { containerRef: toolbarRef, locationCompact, searchCompact } = useResponsiveToolbar();
    usePageTitle('Orders');
    const dashboardViewStateFromLocation = useMemo<DashboardViewStateCache | null>(() => {
        const state = location.state as { dashboardViewState?: DashboardViewStateCache; } | null;
        const viewState = state?.dashboardViewState;
        if (!viewState) return null;

        return {
            locationIds: viewState.locationIds ?? [],
            activeTab: viewState.activeTab ?? 'all',
            currentPage: viewState.currentPage ?? 1,
            searchQuery: viewState.searchQuery ?? '',
            debouncedSearch: viewState.debouncedSearch ?? viewState.searchQuery ?? '',
            additionalFilters: parseCachedAdditionalFilters(viewState.additionalFilters ?? {}),
            sortState: viewState.sortState ?? defaultSortState,
        };
    }, [location.state]);
    const cachedDashboardState = useMemo(
        () => dashboardViewStateFromLocation ?? loadDashboardViewStateCache(),
        [dashboardViewStateFromLocation]
    );
    const [locationIds, setLocationIds] = useState<Array<string> | 'all'>(() => cachedDashboardState?.locationIds ?? []);
    const [activeTab, setActiveTab] = useState<DashboardActiveTab>(() => {
        const storedTab = localStorage.getItem('a_tb') as DashboardActiveTab | null;
        return cachedDashboardState?.activeTab ?? storedTab ?? 'all';
    });
    const dashboardColWidthKey = useMemo(() => TAB_TO_COLUMN_KEY[activeTab] || 'dashboard_all', [activeTab]);
    const { widths: dashboardColWidths, resetWidths: resetDashboardColWidths } = useColumnWidths(dashboardColWidthKey);
    const hasCustomColumnWidths = Object.keys(dashboardColWidths).length > 0;
    const [colWidthsResetKey, setColWidthsResetKey] = useState(0);
    const handleResetColumnWidths = useCallback(() => {
        resetDashboardColWidths();
        setColWidthsResetKey(k => k + 1);
    }, [resetDashboardColWidths]);
    const [currentPage, setCurrentPage] = useState(() => cachedDashboardState?.currentPage ?? 1);
    // The search comes back from a breadcrumb return (location state) or from
    // browser back/forward to this same history entry (in-memory), never from
    // the session cache: a refresh or sidebar click lands on an empty search.
    const [searchQuery, setSearchQuery] = useState(() => (
        dashboardViewStateFromLocation?.searchQuery ?? recallListSearch(location.key) ?? ''
    ));
    const [debouncedSearch, setDebouncedSearch] = useState(() => (
        dashboardViewStateFromLocation?.debouncedSearch
        ?? dashboardViewStateFromLocation?.searchQuery
        ?? recallListSearch(location.key)
        ?? ''
    ));
    // Set search and debounced search together so the debounce sees no local
    // edit and leaves the current page alone (only typing resets to page 1).
    const applySearch = useCallback((search: string, debounced: string = search) => {
        setSearchQuery(search);
        setDebouncedSearch(debounced);
    }, []);
    useRememberListSearch(searchQuery, applySearch);

    useEffect(() => {
        const state = location.state as { dashboardViewState?: Partial<DashboardViewStateCache> } | null;
        const viewState = state?.dashboardViewState;
        if (!viewState) return;
        // Apply the handed-over search even when Orders is already mounted: a
        // breadcrumb return carries the search that got the user to the detail,
        // while a sidebar click on the active tab hands over an empty one.
        const nextSearch = viewState.searchQuery ?? '';
        applySearch(nextSearch, viewState.debouncedSearch ?? nextSearch);
        // Consume the payload so a refresh does not replay the search.
        const { dashboardViewState: _consumed, ...rest } = state;
        navigate(
            { pathname: location.pathname, search: location.search },
            { replace: true, state: rest },
        );
    }, [applySearch, location.pathname, location.search, location.state, navigate]);
    const [additionalFilters, setAdditionalFilters] = useState<AdditionalFilters>(() => {
        if (cachedDashboardState) {
            return cachedDashboardState.additionalFilters ?? {};
        }

        try {
            const saved = localStorage.getItem('dashboard_advanced_filters');
            if (saved) {
                const parsed = JSON.parse(saved);
                // Convert date strings back to Date objects
                if (parsed.lastUpdatedRange) {
                    parsed.lastUpdatedRange = {
                        start: new Date(parsed.lastUpdatedRange.start),
                        end: new Date(parsed.lastUpdatedRange.end)
                    };
                }
                return parsed;
            }
            return {};
        } catch {
            return {};
        }
    });
    const searchChangeIsLocal = useRef(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch((previous) => {
                if (previous === searchQuery) return previous;
                searchChangeIsLocal.current = true;
                return searchQuery;
            });
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        // Only a typed search change resets to page 1; a search handed over by
        // the breadcrumb, sidebar, or history restore keeps the page it came with.
        if (!searchChangeIsLocal.current) return;
        searchChangeIsLocal.current = false;
        setCurrentPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        localStorage.setItem('dashboard_advanced_filters', JSON.stringify(additionalFilters));
    }, [additionalFilters]);

    const [sortState, setSortState] = useState<SortState>(() => {
        if (cachedDashboardState?.sortState) {
            return cachedDashboardState.sortState;
        }

        const savedTab = localStorage.getItem('a_tb') as Phase | 'all' | 'archived' | 'completed' | 're-keying' || 'all';
        return loadSortState(savedTab);
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const cachedState: DashboardViewStateCache = {
            locationIds,
            activeTab,
            currentPage,
            searchQuery,
            debouncedSearch,
            additionalFilters,
            sortState,
        };

        sessionStorage.setItem(DASHBOARD_VIEW_STATE_CACHE_KEY, JSON.stringify(cachedState));
    }, [locationIds, activeTab, currentPage, searchQuery, debouncedSearch, additionalFilters, sortState]);
    const limit = 20;
    // We intentionally fetch one extra record to reliably detect whether a "next" page exists.
    // Without a totalCount/hasMore from the API, `orders.length === limit` is ambiguous.
    const queryLimit = limit + 1;

    const handleActiveTabChange = useCallback((newActiveTab: Phase | 'all' | 'archived' | 'completed' | 're-keying') => {
        setActiveTab(newActiveTab);
        setCurrentPage(1); // Reset to first page when tab changes
        setSearchQuery(''); // Clear search when changing tabs
        setDebouncedSearch('');
        setSortState(loadSortState(newActiveTab)); // Load saved sort for this tab
        localStorage.setItem('a_tb', newActiveTab);
    }, []);

    const dashboardTabs = useMemo(() => {
        const tabs: (Phase | 'all' | 'archived' | 'completed' | 're-keying')[] = [
            'all', 'new-order', 'in-quoting', 'ready-to-order', 'ordered', 'rejected',
            ...(session?.isAuthenticated && session.rekeyingEnabled ? ['re-keying' as const] : []),
            'completed', 'archived'
        ];
        return tabs;
    }, [session?.isAuthenticated, session?.rekeyingEnabled]);

    // Map tab keys to display labels matching the front-end
    const tabDisplayLabels: Record<string, string> = useMemo(() => ({
        'all': 'All',
        'archived': 'Archived',
        'new': 'New Order',
        'quoting': 'Quoting',
        'ready-to-order': 'Ready to Order',
        'ordered': 'Ordered',
        'rejected': 'Rejected',
        'completed': 'Completed',
        're-keying': 'Re-Keying'
    }), []);

    useEffect(() => {
        const tabLabel = tabDisplayLabels[activeTab] || activeTab;
        trackView({
            type: 'dashboard_tab',
            key: `dashboard_tab:${activeTab}`,
            label: `Dashboard • ${tabLabel}`,
            path: '/dashboard',
            meta: { tab: activeTab }
        });
    }, [activeTab, trackView, tabDisplayLabels]);

    const handleLocationChange = useCallback((newLocationIds: Array<string> | 'all') => {
        setLocationIds((previousLocationIds) => {
            if (areLocationSelectionsEqual(previousLocationIds, newLocationIds)) {
                return previousLocationIds;
            }
            setCurrentPage(1); // Reset to first page when location changes
            return newLocationIds;
        });
    }, []);

    const handleFiltersChange = useCallback((newFilters: AdditionalFilters) => {
        setAdditionalFilters((previousFilters) => {
            if (areAdditionalFiltersEqual(previousFilters, newFilters)) {
                return previousFilters;
            }
            setCurrentPage(1);
            return newFilters;
        });
    }, []);

    const handleSortChange = useCallback((field: DashboardSortField) => {
        setSortState(prev => {
            const newState = toggleSort(prev, field);
            saveSortState(activeTab, newState);
            return newState;
        });
    }, [activeTab]);

    const {
        data: ordersData,
        isLoading: ordersLoading,
        isRefetching,
        error: ordersError,
        refetch: refetchOrders
    } = useGetOrdersFeedPaginated(activeTab, locationIds, currentPage, queryLimit, debouncedSearch, additionalFilters);

    const { pageOrders, hasNextPage } = useMemo(() => {
        const fetched = ordersData?.orders || [];
        return {
            pageOrders: fetched.slice(0, limit),
            hasNextPage: fetched.length > limit,
        };
    }, [ordersData?.orders, limit]);

    // Sort filtered orders
    const sortedOrders = useMemo(() => {
        if (!sortState.field) return pageOrders;

        return [...pageOrders].sort((a, b) => {
            let aValue: number;
            let bValue: number;

            const getSortValue = (order: OrderFeedEntry, field: DashboardSortField): number => {
                switch (field) {
                    case 'partsOrdered':
                        if ('partsOrdered' in order) {
                            return (order as AllOrdersEntry | ReadyToOrderEntry | OrderedEntry | CompletedEntry).partsOrdered;
                        }
                        return 0;

                    case 'daysActive':
                        if ('daysOpen' in order) {
                            return parseDays((order as AllOrdersEntry).daysOpen);
                        }
                        return 0;

                    case 'orderValue':
                        if ('orderValue' in order) {
                            return (order as ReadyToOrderEntry | OrderedEntry | CompletedEntry).orderValue || 0;
                        }
                        return 0;

                    case 'grossMargin':
                        if ('grossProfitMargin' in order) {
                            return (order as ReadyToOrderEntry | OrderedEntry | CompletedEntry).grossProfitMargin || 0;
                        }
                        return 0;

                    case 'deliveryTime':
                        // Use parseDeliveryTime for "Ready to Order" (delivery time like "1-2 days")
                        if ('deliveryTime' in order) {
                            return parseDeliveryTime((order as ReadyToOrderEntry).deliveryTime);
                        }
                        // Use parseEtaDate for "Ordered" tab (ETA dates like "Mon, 12/15")
                        if ('eta' in order) {
                            return parseEtaDate((order as OrderedEntry).eta);
                        }
                        // For Completed tab, we sort by completion time
                        // We might want to add a specific case for this or reuse deliveryTime field if the header uses that key
                        // But CompletedTable doesn't use 'deliveryTime' sort key, it likely uses something else or we map it.
                        // Actually CompletedTable in my implementation doesn't have a sortable header for 'COMPLETED DATE' yet because I didn't add the SortableTableHeader there.
                        // Let's assume user might want to sort by date. CompletedEntry has timeCompleted.
                        if ('timeCompleted' in order) {
                            const date = (order as CompletedEntry).timeCompleted;
                            return date ? new Date(date).getTime() : 0;
                        }
                        return Infinity;

                    case 'timeOrdered':
                        if ('timeOrdered' in order) {
                            const date = (order as OrderedEntry).timeOrdered;
                            return date ? new Date(date).getTime() : 0;
                        }
                        return 0;

                    case 'partsToQuote':
                        if ('partsToQuote' in order) {
                            return (order as NewOrderEntry).partsToQuote || 0;
                        }
                        return 0;

                    case 'partsQuoting':
                        if ('partsQuoting' in order) {
                            return (order as QuotingEntry).partsQuoting || 0;
                        }
                        return 0;

                    default:
                        return 0;
                }
            };

            aValue = getSortValue(a, sortState.field);
            bValue = getSortValue(b, sortState.field);

            if (sortState.direction === 'asc') {
                return aValue - bValue;
            } else {
                return bValue - aValue;
            }
        });
    }, [pageOrders, sortState]);

    const showBulkUI = !!session?.isAuthenticated && session.permissions.includes('complete-uncomplete') && session.permissions.includes('archive-unarchive');

    const bulkListContext: OrderBulkListContext =
        activeTab === 'archived' ? 'dashboard-archived' : 'dashboard-active';

    const pageOrderIds = useMemo(
        () => sortedOrders.map((order) => order._id),
        [sortedOrders],
    );

    const {
        selectedOrderIds,
        toggleOrderSelected,
        toggleSelectAllOnPage,
        allPageSelected,
        isPageSelectionIndeterminate,
        clearSelection,
    } = useOrderBulkSelection({ orderIds: pageOrderIds });

    const { runBulkAction, isLoading: isBulkLoading } = useOrderBulkActions(clearSelection);

    const bulkPermissions = useMemo((): OrderBulkPermissions => {
        const base = getOrderBulkPermissions(session?.permissions ?? []);
        return {
            canFlag: false,
            canReview: false,
            canArchive: showBulkUI && base.canArchive,
            canUnarchive: showBulkUI && base.canUnarchive,
            canComplete: showBulkUI && bulkListContext !== 'dashboard-archived' && base.canComplete,
        };
    }, [session?.permissions, bulkListContext, showBulkUI]);

    const selectedOrders = useMemo(
        () => sortedOrders
            .filter((order) => selectedOrderIds.has(order._id))
            .map((order) => ({
                _id: order._id,
                archived: activeTab === 'archived',
                completed: activeTab === 'completed',
                manuallyFlagged: false,
                reviewedByCurrentUser: false,
                canComplete: 'canComplete' in order ? order.canComplete : false,
            })),
        [sortedOrders, selectedOrderIds, activeTab],
    );

    const bulkSelection: DashboardBulkSelection | undefined = showBulkUI
        ? {
            enabled: true,
            selectedOrderIds,
            onToggle: toggleOrderSelected,
            allPageSelected,
            isPageSelectionIndeterminate,
            onToggleSelectAll: toggleSelectAllOnPage,
            pageOrderCount: pageOrderIds.length,
        }
        : undefined;

    useEffect(() => {
        clearSelection();
    }, [activeTab, currentPage, debouncedSearch, locationIds, additionalFilters, clearSelection]);

    const { scrollContainerProps: stickyCopyScrollProps } = useStickyTableCopyFront(
        scrollContainerRef,
        `${ordersLoading}-${sortedOrders.length}-${activeTab}`,
    );

    const totalPages = useMemo(() => {
        // Search is client-side (only within the current page), so we can't reliably paginate search results.
        if (searchQuery.trim()) return currentPage;
        return hasNextPage ? currentPage + 1 : currentPage;
    }, [hasNextPage, currentPage, searchQuery]);

    const renderOrdersTable = () => {
        switch (activeTab) {
            case 'all':
                return (
                    <AllOrdersTable
                        key={colWidthsResetKey}
                        orders={sortedOrders as AllOrdersEntry[]}
                        sortState={sortState}
                        onSortChange={handleSortChange}
                        bulkSelection={bulkSelection}
                    />
                );
            case 'archived':
                return (
                    <ArchivedOrdersTable
                        key={colWidthsResetKey}
                        orders={sortedOrders as AllOrdersEntry[]}
                        sortState={sortState}
                        onSortChange={handleSortChange}
                        bulkSelection={bulkSelection}
                    />
                );
            case 'new-order':
                return (
                    <NewOrderTable
                        key={colWidthsResetKey}
                        orders={sortedOrders as NewOrderEntry[]}
                        sortState={sortState}
                        onSortChange={handleSortChange}
                        bulkSelection={bulkSelection}
                    />
                );
            case 'in-quoting':
                return (
                    <QuotingTable
                        key={colWidthsResetKey}
                        orders={sortedOrders as QuotingEntry[]}
                        sortState={sortState}
                        onSortChange={handleSortChange}
                        bulkSelection={bulkSelection}
                    />
                );
            case 'ready-to-order':
                return (
                    <SelectionTable
                        key={colWidthsResetKey}
                        orders={sortedOrders as ReadyToOrderEntry[]}
                        sortState={sortState}
                        onSortChange={handleSortChange}
                        bulkSelection={bulkSelection}
                    />
                );
            case 'ordered':
                return (
                    <OrderedTable
                        key={colWidthsResetKey}
                        orders={sortedOrders as OrderedEntry[]}
                        sortState={sortState}
                        onSortChange={handleSortChange}
                        bulkSelection={bulkSelection}
                    />
                );
            case 'rejected':
                return (
                    <SelectionTable
                        key={colWidthsResetKey}
                        orders={sortedOrders as ReadyToOrderEntry[]}
                        sortState={sortState}
                        onSortChange={handleSortChange}
                        bulkSelection={bulkSelection}
                    />
                );
            case 'completed':
                return (
                    <CompletedTable
                        key={colWidthsResetKey}
                        orders={sortedOrders as CompletedEntry[]}
                        sortState={sortState}
                        onSortChange={handleSortChange}
                        bulkSelection={bulkSelection}
                    />
                );
            case 're-keying':
                return (
                    <RekeyingTable
                        key={colWidthsResetKey}
                        orders={sortedOrders as ReKeyingEntry[]}
                        bulkSelection={bulkSelection}
                    />
                );
            default:
                return (
                    <AllOrdersTable
                        orders={sortedOrders as AllOrdersEntry[]}
                        sortState={sortState}
                        onSortChange={handleSortChange}
                    />
                );
        }
    };

    return (
        <Box
            w="calc(100% + 2rem)"
            mx={-4}
            mt={-4}
            mb={-2}
            h="calc(100dvh - var(--partbay-topbar))"
            display="flex"
            flexDirection="column"
            pb={0}
            overflow="hidden"
        >
            {/* Row 1 — workflow tabs + toolbar actions, hairline underneath (ENG-2297). */}
            <ListTabRow
                actions={
                    <HStack spacing={1} flexShrink={0}>
                        <TableSettingsButton
                            settings={tableSettings}
                            onUpdateSettings={updateTableSettings}
                            hasCustomColumnWidths={hasCustomColumnWidths}
                            onResetColumnWidths={handleResetColumnWidths}
                        />
                        <RefreshButton
                            refetch={refetchOrders}
                            isLoading={isRefetching}
                        />
                    </HStack>
                }
            >
                    {dashboardTabs.map(el => {
                        const label = el === 'all' ? 'All' :
                            el === 'new-order' ? 'New Order' :
                                el === 'in-quoting' ? 'Quoting' :
                                    el === 'ready-to-order' ? 'Ready to Order' :
                                        el === 'ordered' ? 'Ordered' :
                                            el === 'rejected' ? 'Rejected' :
                                                el === 'completed' ? 'Completed' :
                                                    el === 're-keying' ? 'Re-Keying' :
                                                        el === 'archived' ? 'Archived' :
                                                            String(el)[0].toUpperCase() + String(el).slice(1);
                        const tabIcon = el === 'all' ? FiList :
                            el === 'new-order' ? FiFileText :
                                el === 'in-quoting' ? FiDollarSign :
                                    el === 'ready-to-order' ? FiShoppingCart :
                                        el === 'ordered' ? FiPackage :
                                            el === 'rejected' ? FiAlertTriangle :
                                                el === 're-keying' ? FiRefreshCw :
                                                    el === 'completed' ? FiCheckCircle :
                                                        el === 'archived' ? TbArchive :
                                                            null;
                        const isActive = activeTab === el;
                        const numRejected = ordersData?.numRejected ?? 0;
                        return (
                            <Box key={el} position="relative" display="inline-flex" alignItems="center">
                                {el === 'rejected' ? (
                                    <PendingCountBadge count={numRejected} />
                                ) : null}
                                <Button
                                    size="sm"
                                    h="32px"
                                    px={4}
                                    borderRadius="full"
                                    fontWeight="medium"
                                    fontSize="sm"
                                    flexShrink={0}
                                    bg={isActive ? 'brand.solid' : 'transparent'}
                                    color={isActive ? 'white' : 'gray.600'}
                                    _hover={{
                                        bg: isActive ? 'brand.solid' : 'gray.100',
                                    }}
                                    _active={{
                                        bg: isActive ? 'brand.solid' : 'gray.100',
                                    }}
                                    onClick={() => handleActiveTabChange(el)}
                                    {...(tabIcon ? { leftIcon: <Icon as={tabIcon as React.ElementType} boxSize={3.5} /> } : {})}
                                >
                                    {label}
                                </Button>
                            </Box>
                        );
                    })}
            </ListTabRow>

            {/* Row 2 — search + filters left, location far right (ENG-2297 #4). */}
            <Flex
                ref={toolbarRef}
                my={3}
                px={6}
                flexShrink={0}
                align="center"
                gap={2}
            >
                <CollapsibleSearch
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by VIN or RO#..."
                    isCompact={searchCompact}
                    width={LIST_PAGE_SEARCH_WIDTH}
                />
                <AdvancedFilters
                    filters={additionalFilters}
                    onFiltersChange={handleFiltersChange}
                    showMyQuotes={true}
                />
                <Box ml="auto" flexShrink={0}>
                    <LocationSelector
                        storageKey="location_ids"
                        onLocationChange={handleLocationChange}
                        width="260px"
                        icon={TbBuilding}
                        iconOnly={locationCompact}
                    />
                </Box>
            </Flex>

            {/* Table section - fills remaining space */}
            <Box
                flex={1}
                display="flex"
                flexDirection="column"
                overflow="hidden"
                minH={0}
            >
                {ordersLoading || !ordersData ? (
                    <Flex justify="center" align="center" h="400px">
                        <Spinner size="xl" color="blue.500" />
                    </Flex>
                ) : ordersError ? (
                    <Alert status="error">
                        <AlertIcon />
                        Failed to load orders feed. Please try again.
                    </Alert>
                ) : sortedOrders.length > 0 ? (
                    <>
                        <Box position="relative" flex={1} minH={0} display="flex" flexDirection="column">
                            <Box
                                ref={scrollContainerRef}
                                {...stickyCopyScrollProps}
                                flex={1}
                                minH={0}
                                overflow="auto"
                                borderTop="1px solid"
                                borderColor="gray.200"
                                sx={{
                                    ...tableSettingsSx,
                                    ...stickyTableScrollCopySx,
                                    overscrollBehavior: 'contain',
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#cbd5e0 transparent',
                                    '& table': {
                                        width: 'max-content !important',
                                        minWidth: '100%',
                                    },
                                    '& th': {
                                        ...(tableSettingsSx['& th'] || {}),
                                        borderBottomWidth: '0 !important',
                                        boxShadow: 'inset 0 -1px 0 0 var(--chakra-colors-gray-200)',
                                        bg: 'gray.50',
                                    },
                                    '& th:first-of-type': {
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 5,
                                        bg: 'gray.50',
                                    },
                                    '& td:first-of-type': {
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 2,
                                        background: 'inherit',
                                    },
                                }}
                            >
                                {renderOrdersTable()}
                            </Box>
                            {showRightShadow && (
                                <Box
                                    position="absolute"
                                    right={0}
                                    top={0}
                                    bottom={0}
                                    w="48px"
                                    bgGradient="linear(to-r, rgba(255,255,255,0), rgba(255,255,255,0.85))"
                                    pointerEvents="none"
                                    zIndex={4}
                                />
                            )}
                            {showBulkUI && selectedOrderIds.size > 0 && (
                                <OrderBulkActionBar
                                    selectedOrders={selectedOrders}
                                    permissions={bulkPermissions}
                                    onClearSelection={clearSelection}
                                    onAction={(actionId) => void runBulkAction(actionId, Array.from(selectedOrderIds))}
                                    isLoading={isBulkLoading}
                                />
                            )}
                        </Box>
                        <Box flexShrink={0} borderTop="1px solid" borderColor="gray.200">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                isLoading={ordersLoading || isRefetching}
                                hasNextPage={hasNextPage}
                            />
                        </Box>
                    </>
                ) : (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flex={1}
                        borderTop="1px solid"
                        borderColor="gray.200"
                    >
                        <VStack spacing={2}>
                            <Text fontSize="md" color="gray.500">
                                {searchQuery ? 'No matching orders found' : 'No orders found'}
                            </Text>
                            <Text fontSize="sm" color="gray.400">
                                {searchQuery ? 'Try a different search term' : 'Orders will appear here once you upload EMS files'}
                            </Text>
                        </VStack>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default Orders;